import posthog from "posthog-js";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import {
  routeGroupForPathname,
  routePatternForPathname,
  routeSectionForPathname,
  scrubUrl,
} from "@/utils/analytics-page";

const projectToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
const runtimeEnvironment = getRuntimeEnvironment();

function hasAnalyticsConsent(): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const status = window.localStorage.getItem("fnp_cookie_consent");
    if (status === "accepted") return true;
    if (status !== "custom") return false;
    const cats = JSON.parse(
      window.localStorage.getItem("fnp_cookie_categories") ?? "{}",
    ) as { analytics?: boolean; marketing?: boolean };
    // PostHog is measurement → analytics flag (marketing-only is not enough).
    return cats.analytics === true;
  } catch {
    return false;
  }
}

const pathnameFromProperties = (
  properties: Record<string, any> | undefined,
): string | null => {
  const raw =
    (typeof properties?.["$pathname"] === "string" && properties["$pathname"]) ||
    (typeof properties?.["$current_url"] === "string" && properties["$current_url"]) ||
    (typeof window !== "undefined" ? window.location.href : null);
  if (!raw) return null;
  try {
    return new URL(raw, window.location.origin).pathname;
  } catch {
    return raw.split("?")[0].split("#")[0] || null;
  }
};

/**
 * Single enrichment point for every outbound PostHog event (pageviews and
 * custom events alike). Never drops data: on any failure the event passes
 * through unmodified.
 */
const enrichAnalyticsEvent = (data: any): any => {
  try {
    if (!data || typeof data !== "object") return data;
    const properties = data.properties;
    if (!properties || typeof properties !== "object") return data;

    // Scrub first: tokens and identifiers must never reach PostHog.
    if (typeof properties["$current_url"] === "string") {
      properties["$current_url"] = scrubUrl(properties["$current_url"]);
    }

    const pathname = pathnameFromProperties(properties);
    if (pathname) {
      properties["route_group"] = routeGroupForPathname(pathname);
      properties["route_section"] = routeSectionForPathname(pathname);
      const pattern = routePatternForPathname(pathname);
      if (pattern) properties["route_pattern"] = pattern;
    }

    // auth_state is registered as a super property on login (see
    // PostHogAnalyticsProvider) and cleared by reset() on logout.
    if (properties["auth_state"] == null) {
      properties["auth_state"] = "anonymous";
    }
  } catch {
    /* enrichment must never break capture */
  }
  return data;
};

if (typeof window !== "undefined" && projectToken) {
  posthog.init(projectToken, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    defaults: "2026-05-30",
    capture_pageview: false,
    // Time-on-page / scroll depth. The default "if_capture_pageview" would
    // stay off while capture_pageview is false, so this is explicit.
    capture_pageleave: true,
    persistence: "localStorage",
    // Consent-first: start opted out; CookieConsentProvider opts in on accept.
    opt_out_capturing_by_default: true,
    before_send: enrichAnalyticsEvent,
    session_recording: {
      // Form field values (search boxes, KYC name fields inside the profile
      // modal — KYC has no dedicated route) are never recorded.
      maskAllInputs: true,
    },
  });

  // Static session context on every event (merged under per-event values,
  // never overriding them). auth_state is managed separately (registered
  // authenticated on login, cleared by reset() on logout).
  try {
    posthog.register({
      app_env: runtimeEnvironment.isProduction
        ? "production"
        : runtimeEnvironment.isStaging
          ? "staging"
          : "development",
    });
  } catch {
    /* noop */
  }

  if (hasAnalyticsConsent()) {
    try {
      posthog.opt_in_capturing();
    } catch {
      /* noop */
    }
    posthog.capture("$pageview", {
      $current_url: window.location.href,
    });
  }
}
